import {makeTrackEvent} from './makeTrackEvent';

export type TrackPerfAction = 'customer_ready' | 'data_fetched';

export type TrackPerfEvent = {
  pageName: string;
  action: TrackPerfAction;
  uid?: string;
  elapsed?: number;
  duration?: number;
  objectType?: string;
  pageNumber?: number;
  response?: 'cache-hit' | 'cache-miss';
  additionalData?: Record<string, unknown>;
};

const trackPerfMetrics = makeTrackEvent<
  TrackPerfEvent,
  {
    pageName: {
      description: 'The product page where this perf event occurred.';
      exampleValues: ['Artifact Lineage', 'Registry Details'];
    };
    action: {
      description: 'The perf lifecycle action that occurred.';
      exampleValues: ['customer_ready', 'data_fetched'];
    };
    uid: {
      description: 'A route-view scoped identifier for joining related perf events.';
      exampleValues: ['b7a59b1b-9d5f-4ad9-9e6b-45b1c7f1fa13'];
    };
    elapsed: {
      description: 'Milliseconds since the route-view perf session started.';
      exampleValues: [2450];
    };
    duration: {
      description: 'Operation duration in milliseconds when applicable.';
      exampleValues: [180, 920];
    };
    objectType: {
      description: 'Entity type fetched for data_fetched events.';
      exampleValues: ['collections', 'runs'];
    };
    pageNumber: {
      // Using pageNumber due to limited allowlisted properties from Analytics
      description: 'The number of results returned.';
      exampleValues: [1, 10];
    };
    response: {
      description: 'Whether data was served from cache or required a miss.';
      exampleValues: ['cache-hit', 'cache-miss'];
    };
    additionalData: {
      description: 'Additional contextual properties for this perf event.';
      exampleValues: [{organizationName: '<name>'; tab: 'collections'}];
    };
    _description: 'Tracks route-view scoped performance events across app pages.';
    _location: 'app';
    _motivation: 'Measure user-perceived performance and correlate perf actions across a route view.';
  }
>('Perf metrics');

export function trackPerfEvent(eventData: TrackPerfEvent): void {
  trackPerfMetrics(eventData);
}
